<script>
    import { getContext } from 'svelte';
    import { Policy } from "@Store";
    import Question from "@components/Question.svelte";
    import 'animate.css';
    import { computeOfficePackageValid } from '@routes/validators/validator';
    const CONTEXT = getContext('CONTEXT');

    $: questions = $Policy.questions ? $Policy.questions : [];
    // APPLICANT QUESTIONS SECTION

    // Create a lookup map in one pass - much more efficient!
    $: questionMap = questions.reduce((map, question) => {
        map[question.question] = question;
        return map;
    }, {});

    // Now you can access any question efficiently
    $: ({
        'Do you require commercial General Liability and Property Insurance?': general_liability_and_property_insurance_question,
        'Commercial General Liability Limit (CGL)': cgl_limit_question,
        'Location Address (CGL)': location_address_question,
        'Do you practice at multiple locations?': cgl_operate_diff_location_question,
        'Please list all locations': all_locations_question,
        'Do you require any additional insureds to be listed on your policy?': additional_insureds_question,
        'Additional Insured Name(s)': additional_insured_name_question,
        'Additional Insured Address(es)': additional_insured_address_question,
        'Are you a sole practitioner or a clinic?': sole_practitioner_or_clinic_question,
        'Commercial General Liability Limit (CGL & Property)': cgl_and_property_limit_question,
        'Location Address (CGL & Property)': cgl_and_property_location_address_question,
        'Do you have multiple locations? (CGL & Property)': cgl_and_property_multiple_locations_question,
        'Please list all locations (CGL & Property)': cgl_and_property_all_locations_question,
        'Do you require any additional insureds to be listed on your policy? (CGL & Property)': cgl_and_property_additional_insureds_question,

        'Additional Insured Name(s) (CGL & Property)': cgl_and_property_additional_insureds_name_question,
        'Additional Insured Address(es) (CGL & Property)': cgl_and_property_additional_insureds_address_question,

        'Year Built': year_built_question,
        'Square Footage': square_footage_question,
        'Construction': construction_type_question,
        'Number of Storeys': number_of_storeys_question,

        'Do you lease or Own your Premises?': lease_or_own_premises_question,
        'Is the building more than 40 years old?': building_more_than_40_years_old_question,

        'Roof': roof_question,
        'Plumbing': plumbing_question,
        'Heating': heating_question,
        'Electrical': electrical_question,

        'Distance to Hydrants is less than 300m?': distance_to_hydrants_less_than_300m_question,
        'Distance to Firehall is less than 8km?': distance_to_firehall_less_than_8km_question,
        '100% Sprinkled?': hundred_percent_sprinkled_question,
        'Burglar Alarm?': burglar_alarm_question,

        'Please select Property Limit (includes office contents, stock & tenants leasehold improvements)': property_limit_question,
        'Contents (equipment, fixtures, inventory)': contents_question,
        'Stock': stock_question,
        'Do you have stock that requires stock spoilage coverage?': stock_spoilage_question,
        'Tenant/Leasehold Improvements': tenants_leasehold_improvements_question,

        'Building Limit': building_limit_question,
        'Is equipment taken off premises': is_equipment_taken_off_premises_question,
        'Please provide total value of mobile equipment': please_provide_total_value_of_mobile_equipment_question,
        'Crime Limit - Employee Dishonesty': crime_limit_employee_dishonesty_question,
        'Crime Limit - Money & Securities': crime_limit_money_and_securities_question,
        'Do you require a mortgagee/loss payee added to your policy?': mortgagee_loss_payee_question,
        'Legal name': legalname_question,
        'Mailing Address': mailingaddress_question,

        'Do you wish to insure building': insure_building_question,
    } = questionMap);

    // Create named values lookup map - ONE iteration instead of 9!
    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    // Helpers to read answers by Question object
    $: getAnswerText = (q) => (q && $Policy.answers?.[q.id]?.text_answer || '').trim();

    $: cgl_only = namedValuesMap['office_coverage']?.toLowerCase().includes('cgl only (individual practitioner)');
    $: show_all_locations_question = namedValuesMap['cgl_multi_location']?.toLowerCase().includes('yes');
    $: show_additional_insured_questions = namedValuesMap['require_ai']?.toLowerCase().includes('yes');

    // CGL and property
    $: sole_practitioner = getAnswerText(sole_practitioner_or_clinic_question) === 'Sole Practitioner' || false;

    // setting up CGL only option  
    $: med_required = namedValuesMap['med_required']?.toLowerCase().includes('yes');
    $: type_of_coverage = namedValuesMap['type_of_coverage']?.toLowerCase();

    $: show_dale_add_on = med_required && type_of_coverage && (type_of_coverage.includes('both') || type_of_coverage.includes('individual'));

    $: cgl_and_property = namedValuesMap['office_coverage']?.toLowerCase().includes('cgl & property');
    $: show_ai_cgl_and_property_questions = namedValuesMap['multi_location_cgl_property']?.toLowerCase().includes('yes');
    $: show_cgl_and_property_additional_insureds_questions = namedValuesMap['require_cgl_property_ai']?.toLowerCase().includes('yes');

    $: owned = namedValuesMap['lease_or_own']?.toLowerCase().includes('own building') || namedValuesMap['lease_or_own']?.toLowerCase().includes('own condo');
    $: show_updated_year_questions = namedValuesMap['over_40']?.toLowerCase().includes('yes');

    $: show_mobile_equipment_questions = namedValuesMap['taken_off_premises']?.toLowerCase().includes('yes');
    $: show_mortgagee_loss_payee_questions = namedValuesMap['require_mortgagee']?.toLowerCase().includes('yes');

    $: show_insure_building_question = namedValuesMap['building_insurance']?.toLowerCase().includes('yes');


    $: stock_above_0 = getAnswerText(stock_question) > 0;

    function parsePropertyLimit(limitText) {
        if (!limitText) return 0;
        // Extract number from strings like "$15,000", "$50,000", etc.
        const cleaned = limitText.replace(/[$,]/g, '');
        return parseInt(cleaned, 10) || 0;
    }

    function parseCurrencyAnswer(answer) {
        if (!answer) return 0;
        // Currency answers are stored as raw numbers without formatting
        const cleaned = String(answer).replace(/[$,]/g, '');
        return parseInt(cleaned, 10) || 0;
    }

    // Get the selected property limit
    $: selectedPropertyLimit = parsePropertyLimit(getAnswerText(property_limit_question));

    // Get the sum of the three breakdown questions
    $: contentsValue = parseCurrencyAnswer(getAnswerText(contents_question));
    $: stockValue = parseCurrencyAnswer(getAnswerText(stock_question));
    $: leaseholdValue = parseCurrencyAnswer(getAnswerText(tenants_leasehold_improvements_question));
    $: propertyBreakdownSum = contentsValue + stockValue + leaseholdValue;

    // Validation state
    $: propertyBreakdownValid = selectedPropertyLimit > 0 && propertyBreakdownSum === selectedPropertyLimit;
    $: showPropertyValidation = selectedPropertyLimit > 0 && (contentsValue > 0 || stockValue > 0 || leaseholdValue > 0);

    // Format number as currency for display
    function formatAsCurrency(num) {
        return num.toLocaleString('en-US');
    }

    
    $: office_package_valid = computeOfficePackageValid($Policy);
    // Update CONTEXT when validation changes
    $: {
        CONTEXT.routeValidation.update(validation => ({
            ...validation,
            officepackage: office_package_valid
        }));
    }

    function handleAnswer(e) {
        CONTEXT.handleAnswer(e);
    }


</script>

<div class="page-container">
    <div class="questions-wrapper">
        <!-- APPLICANT QUESTIONS -->
        {#if general_liability_and_property_insurance_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={general_liability_and_property_insurance_question} remove_options={!show_dale_add_on ? [0] : []} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}

        {#if cgl_only}
            <div class="animate__animated animate__fadeInUp">
                <Question question={cgl_limit_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
            {#if location_address_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={location_address_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if cgl_operate_diff_location_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={cgl_operate_diff_location_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if show_all_locations_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={all_locations_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if additional_insureds_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={additional_insureds_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
                {#if show_additional_insured_questions}
                    <div class="section-container animate__animated animate__fadeInUp">
                        <div class="section-header">
                            <div class="section-icon">�</div>
                            <h1>Please provide legal name and mailing address of additional insured</h1>
                        </div>
                        
                        <div class="sub-questions">
                            <div class="sub-question">
                                <Question question={additional_insured_name_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
    
                            <div class="sub-question">
                                <Question question={additional_insured_address_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        </div>
                    </div>
                {/if}
            {/if}
        {/if}

        {#if cgl_and_property}
            <div class="animate__animated animate__fadeInUp">
                <Question question={sole_practitioner_or_clinic_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>

            {#if cgl_and_property_limit_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={cgl_and_property_limit_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}

            {#if cgl_and_property_location_address_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={cgl_and_property_location_address_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}

            {#if cgl_and_property_multiple_locations_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={cgl_and_property_multiple_locations_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if show_ai_cgl_and_property_questions}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={cgl_and_property_all_locations_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if cgl_and_property_additional_insureds_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={cgl_and_property_additional_insureds_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
                {#if show_cgl_and_property_additional_insureds_questions}
                    <div class="section-container animate__animated animate__fadeInUp">
                        <div class="section-header">
                            <div class="section-icon">�</div>
                            <h1>Please provide legal name and mailing address of additional insured</h1>
                        </div>
                        
                        <div class="sub-questions">
                            <div class="sub-question">
                                <Question question={cgl_and_property_additional_insureds_name_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>

                            <div class="sub-question">
                                <Question question={cgl_and_property_additional_insureds_address_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        </div>
                    </div>
                {/if}
                      
            {#if year_built_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={year_built_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if square_footage_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={square_footage_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if construction_type_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={construction_type_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if number_of_storeys_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={number_of_storeys_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if lease_or_own_premises_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={lease_or_own_premises_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
                                        
                {#if owned}
                    {#if insure_building_question}
                        <div class="animate__animated animate__fadeInUp">
                            <Question question={insure_building_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                    {/if}
                    {#if show_insure_building_question}
                        {#if building_limit_question}
                            <div class="animate__animated animate__fadeInUp">
                                <Question question={building_limit_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}
                    {/if}

                    <div class="animate__animated animate__fadeInUp">
                        <Question question={building_more_than_40_years_old_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                    

                    {#if show_updated_year_questions}
                        <div class="section-container animate__animated animate__fadeInUp">
                            <div class="section-header">
                                <div class="section-icon">🦺</div>
                                <h1>Please provide the year updated:</h1>
                            </div>
                            
                            <div class="sub-questions">
                                <div class="sub-question">
                                    <Question question={roof_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                </div>
        
                                <div class="sub-question">
                                    <Question question={plumbing_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                </div>
                                <div class="sub-question">
                                    <Question question={heating_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                </div>
                                <div class="sub-question">
                                    <Question question={electrical_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                </div>
                            </div>
                        </div>
                    {/if}
                {/if}

            {#if distance_to_hydrants_less_than_300m_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={distance_to_hydrants_less_than_300m_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if distance_to_firehall_less_than_8km_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={distance_to_firehall_less_than_8km_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if hundred_percent_sprinkled_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={hundred_percent_sprinkled_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if burglar_alarm_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={burglar_alarm_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if property_limit_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={property_limit_question} remove_options={sole_practitioner ? [2,3,4] : [0]} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if contents_question && stock_question && tenants_leasehold_improvements_question}
                <div class="section-container animate__animated animate__fadeInUp">
                    <div class="section-header">
                        <div class="section-icon">📊</div>
                        <h1>Property Breakdown (Total must equal above selected property limit)</h1>
                    </div>
                    
                    <div class="sub-questions">
                        <div class="sub-question">
                            <Question question={contents_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>

                        <div class="sub-question">
                            <Question question={stock_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                        {#if stock_above_0}
                            <div class="sub-question">
                                <Question question={stock_spoilage_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}
                        <div class="sub-question">
                            <Question question={tenants_leasehold_improvements_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                    </div>

                    {#if showPropertyValidation}
                        <div class="property-breakdown-validation" class:valid={propertyBreakdownValid} class:invalid={!propertyBreakdownValid}>
                            <div class="validation-row">
                                <span class="validation-label">Selected Property Limit:</span>
                                <span class="validation-value">${formatAsCurrency(selectedPropertyLimit)}</span>
                            </div>
                            <div class="validation-row">
                                <span class="validation-label">Your Breakdown Total:</span>
                                <span class="validation-value">${formatAsCurrency(propertyBreakdownSum)}</span>
                            </div>
                            <div class="validation-row difference">
                                <span class="validation-label">
                                    {#if propertyBreakdownSum > selectedPropertyLimit}
                                        Over by:
                                    {:else if propertyBreakdownSum < selectedPropertyLimit}
                                        Remaining:
                                    {:else}
                                        Difference:
                                    {/if}
                                </span>
                                <span class="validation-value">${formatAsCurrency(Math.abs(selectedPropertyLimit - propertyBreakdownSum))}</span>
                            </div>
                            {#if propertyBreakdownValid}
                                <div class="validation-badge valid-badge">
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    Totals Match
                                </div>
                            {:else}
                                <div class="validation-badge invalid-badge">
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M7 4.66667V7M7 9.33333H7.00667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5"/>
                                    </svg>
                                    Must equal ${formatAsCurrency(selectedPropertyLimit)}
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}
            
            {#if is_equipment_taken_off_premises_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={is_equipment_taken_off_premises_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
                {#if show_mobile_equipment_questions}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={please_provide_total_value_of_mobile_equipment_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                                                                            <!-- {#if mobile_equipment_total_value_answered} -->
                {#if crime_limit_employee_dishonesty_question}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={crime_limit_employee_dishonesty_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                {#if crime_limit_money_and_securities_question}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={crime_limit_money_and_securities_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                {#if mortgagee_loss_payee_question}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={mortgagee_loss_payee_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                    {#if show_mortgagee_loss_payee_questions}
                        <div class="section-container animate__animated animate__fadeInUp">
                            <div class="section-header">
                                <div class="section-icon">📊</div>
                                <h1>Please provide legal name and mailing address of mortgagee/loss payee</h1>
                            </div>
                            <div class="sub-questions">
                                <div class="sub-question">
                                    <Question question={legalname_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                </div>
        
                                <div class="sub-question">
                                    <Question question={mailingaddress_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                </div>
                            </div>
                        </div>
                    {/if}

        {/if}
    </div>
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .questions-wrapper {
        width: 100%;
        max-width: 500px;
        margin-bottom: 2rem;
    }

    /* Section Container Styles */
    .section-container {
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-sizing: border-box;
        padding: 2rem 1.5rem;
        margin: 2rem 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
    }

    .section-icon {
        font-size: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }

    .section-header h1 {
        font-size: 1.375rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .sub-questions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .sub-question {
        position: relative;
    }

    @media (max-width: 768px) {
        .section-container {
            padding: 1.5rem 1rem;
            border-radius: 16px;
        }

        .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .section-header h1 {
            font-size: 1.25rem;
        }

        .section-icon {
            width: 48px;
            height: 48px;
            font-size: 1.75rem;
        }

        .sub-question {
            padding-left: 1rem;
        }

        .sub-question::before {
            width: 3px;
        }
    }

    /* Property Breakdown Validation Styles */
.property-breakdown-validation {
    margin-top: 1.5rem;
    padding: 1.25rem;
    border-radius: 12px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    transition: all 0.3s ease;
}

.property-breakdown-validation.valid {
    background: linear-gradient(135deg, rgba(49, 104, 50, 0.05) 0%, rgba(49, 104, 50, 0.02) 100%);
    border-color: var(--base-green, #316832);
}

.property-breakdown-validation.invalid {
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.02) 100%);
    border-color: var(--red, #dc3545);
}

.validation-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.validation-row:last-of-type {
    border-bottom: none;
}

.validation-row.difference {
    font-weight: 600;
    padding-top: 0.75rem;
    margin-top: 0.25rem;
    border-top: 2px solid rgba(0, 0, 0, 0.1);
}

.validation-label {
    font-size: 0.9375rem;
    color: #555;
}

.validation-value {
    font-size: 1rem;
    font-weight: 600;
    color: #1d1d1f;
    font-family: -apple-system, BlinkMacSystemFont, "SF Mono", "Segoe UI Mono", monospace;
}

.validation-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 600;
}

.validation-badge.valid-badge {
    background: var(--base-green, #316832);
    color: white;
}

.validation-badge.invalid-badge {
    background: var(--red, #dc3545);
    color: white;
}

@media (max-width: 480px) {
    .property-breakdown-validation {
        padding: 1rem;
    }
    
    .validation-label,
    .validation-value {
        font-size: 0.875rem;
    }
}
</style>